import {isBefore} from 'date-fns';
import {DataStatus, Form, FormField, ValidationErrors} from 'platform/components';
import {Grid, GridItem, VStack} from 'platform/foundation';

import {UseFormReset, UseFormReturn} from 'react-hook-form';

import {defaultTo} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {useGetManufacturersQuery} from '@dms/api/core';
import {useGetSuppliersQuery, useGetWarehousesQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  DateRangeField,
  DEFAULT_RANGE_VALUE,
  formatExternalFiltersDate,
  getOptionsFromManufacturers,
  getOptionsFromSuppliers,
  getOptionsFromWarehouses,
  RangeField,
} from '@dms/shared';
import {NumberRangeField} from '@dms/warehouse/shared';

import {suffixTestId, TestIdProps, parseDate} from 'shared/web';

import {FormToDataGridConnectorProps} from 'features/eag-grid';

type PurchaseListExternalFilterForm = Partial<{
  warehouseId: string;
  manufacturerId: string;
  supplierId: string;
  created: DateRangeField;
  neededAt: DateRangeField;
  neededIn: NumberRangeField;
}>;

type PurchaseListExternalFilterProps = FormToDataGridConnectorProps & TestIdProps;

function isDateRangeField(value: unknown): value is DateRangeField {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  return 'from' in value || 'to' in value;
}

function isNumberRangeField(value: unknown): value is NumberRangeField {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  return 'from' in value || 'to' in value;
}

export function PurchaseListExternalFilter(props: PurchaseListExternalFilterProps) {
  const {
    data: warehouses,
    isLoading: areWarehousesLoading,
    isError: hasWarehousesError,
  } = useGetWarehousesQuery();

  const {
    data: manufacturers,
    isLoading: areManufacturersLoading,
    isError: hasManufacturersError,
  } = useGetManufacturersQuery();

  const {
    data: suppliers,
    isLoading: areSuppliersLoading,
    isError: hasSuppliersError,
  } = useGetSuppliersQuery();

  const isLoading = areWarehousesLoading || areManufacturersLoading || areSuppliersLoading;

  const hasError = hasWarehousesError || hasManufacturersError || hasSuppliersError;

  const rawCreated = props.values?.created;
  const rawNeededAt = props.values?.neededAt;
  const rawNeededIn = props.values?.neededIn;
  const rawWarehouseId = props.values.warehouseId;

  const values: PurchaseListExternalFilterForm = {
    ...props.values,
    created: defaultTo(DEFAULT_RANGE_VALUE, isDateRangeField(rawCreated) ? rawCreated : undefined),
    neededAt: defaultTo(
      DEFAULT_RANGE_VALUE,
      isDateRangeField(rawNeededAt) ? rawNeededAt : undefined
    ),
    neededIn: defaultTo(
      DEFAULT_RANGE_VALUE,
      isNumberRangeField(rawNeededIn) ? rawNeededIn : undefined
    ),
    warehouseId: isArray(rawWarehouseId)
      ? rawWarehouseId[0]
      : typeof rawWarehouseId === 'string'
        ? rawWarehouseId
        : undefined,
  };

  const handleChange = (
    values: PurchaseListExternalFilterForm,
    _setErrors: (errors: ValidationErrors | null) => void,
    _reset: UseFormReset<PurchaseListExternalFilterForm>,
    formApi: UseFormReturn<PurchaseListExternalFilterForm>
  ) => {
    const warehouseId = formApi.getValues('warehouseId');
    const created = formApi.getValues('created');
    const neededAt = formApi.getValues('neededAt');

    const isWarehouseIdInvalid = isNilOrEmpty(warehouseId);

    if (isWarehouseIdInvalid) {
      formApi.setError('warehouseId', {
        message: i18n.t('page.warehousePurchaseList.label.warehouseValidationMessage'),
      });
    } else {
      formApi.clearErrors('warehouseId');
    }

    const isCreatedInvalid =
      created?.to && created?.from && isBefore(parseDate(created.to), parseDate(created.from));

    if (isCreatedInvalid) {
      formApi.setError('created.from', {
        message: i18n.t('entity.warehouse.labels.wrongDateRangeFormat'),
      });
      formApi.setError('created.to', {
        message: i18n.t('entity.warehouse.labels.wrongDateRangeFormat'),
      });
    } else {
      formApi.clearErrors('created.from');
      formApi.clearErrors('created.to');
    }

    const isNeededAtInvalid =
      neededAt?.to && neededAt?.from && isBefore(parseDate(neededAt.to), parseDate(neededAt.from));

    if (isNeededAtInvalid) {
      formApi.setError('neededAt.from', {
        message: i18n.t('entity.warehouse.labels.wrongDateRangeFormat'),
      });
      formApi.setError('neededAt.to', {
        message: i18n.t('entity.warehouse.labels.wrongDateRangeFormat'),
      });
    } else {
      formApi.clearErrors('neededAt.from');
      formApi.clearErrors('neededAt.to');
    }

    const isFormInvalid = isWarehouseIdInvalid || isCreatedInvalid || isNeededAtInvalid;

    if (isFormInvalid) {
      return;
    }

    const formattedNeededAt = {
      from: formatExternalFiltersDate(values.neededAt?.from),
      to: formatExternalFiltersDate(values.neededAt?.to),
    };

    const formattedCreated = {
      from: formatExternalFiltersDate(values.created?.from),
      to: formatExternalFiltersDate(values.created?.to),
    };

    props.onChange({
      ...values,
      neededAt: formattedNeededAt,
      created: formattedCreated,
    });
  };

  return (
    <DataStatus isLoading={isLoading} isError={hasError}>
      <Form<PurchaseListExternalFilterForm>
        values={values}
        defaultValues={{}}
        onChange={handleChange}
        mode="all"
      >
        {(control, formApi) => {
          const warehouseId = formApi.watch('warehouseId');

          if (!warehouseId) {
            formApi.setError('warehouseId', {
              message: i18n.t('page.warehousePurchaseList.label.warehouseValidationMessage'),
            });
          } else {
            formApi.clearErrors('warehouseId');
          }

          return (
            <VStack spacing={4} height="100%">
              <Grid columns={2}>
                <GridItem span={2}>
                  <FormField
                    isRequired
                    isNotClearable
                    control={control}
                    name="warehouseId"
                    type="choice"
                    label={i18n.t('entity.warehouse.labels.warehouse')}
                    options={getOptionsFromWarehouses(warehouses)}
                    data-testid={suffixTestId('inputs.requestingWarehouseId', props)}
                  />
                </GridItem>
                <GridItem span={2}>
                  <FormField
                    control={control}
                    name="manufacturerId"
                    type="multiChoice"
                    label={i18n.t('entity.warehouse.labels.manufacturer')}
                    options={getOptionsFromManufacturers(manufacturers)}
                    data-testid={suffixTestId('inputs.manufacturerId', props)}
                  />
                </GridItem>
                <GridItem span={2}>
                  <FormField
                    control={control}
                    name="supplierId"
                    type="multiChoice"
                    label={i18n.t('entity.warehouse.labels.articleDefaultSupplier')}
                    options={getOptionsFromSuppliers(suppliers)}
                    data-testid={suffixTestId('inputs.supplierId', props)}
                  />
                </GridItem>
                <GridItem span={2}>
                  <RangeField
                    control={control}
                    type="date"
                    name="created"
                    labelFrom={i18n.t('general.labels.createdFrom')}
                    labelTo={i18n.t('general.labels.createdTo')}
                    data-testid={suffixTestId('inputs.created', props)}
                  />
                </GridItem>
                <GridItem span={2}>
                  <RangeField
                    control={control}
                    type="date"
                    name="neededAt"
                    labelFrom={i18n.t('general.labels.neededAtFrom')}
                    labelTo={i18n.t('general.labels.neededAtTo')}
                    data-testid={suffixTestId('inputs.neededAt', props)}
                  />
                </GridItem>
                <GridItem span={2}>
                  <RangeField
                    control={control}
                    type="number"
                    name="neededIn"
                    labelFrom={i18n.t('general.labels.neededInFrom')}
                    labelTo={i18n.t('general.labels.neededInTo')}
                    data-testid={suffixTestId('inputs.neededIn', props)}
                  />
                </GridItem>
              </Grid>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
